// apps/lk/src/lib/insights.ts
// Преобразует сырые баллы в человеческие тексты.
// Принцип: не «68», а «Вы спокойнее 6 из 10 человек».
// Каждый инсайт = 1-2 предложения + (опционально) конкретный совет.

// ── Уровень (percentile → понятное описание) ─────────────────
function level(pct: number): { word: string; ofTen: number } {
  const ofTen = Math.round(pct / 10);
  if (pct >= 85) return { word: 'очень высоко', ofTen };
  if (pct >= 65) return { word: 'высоко', ofTen };
  if (pct >= 40) return { word: 'умеренно', ofTen };
  if (pct >= 20) return { word: 'низко', ofTen };
  return { word: 'очень низко', ofTen };
}

// ── Big Five домены ──────────────────────────────────────────
type DomainKey = 'O' | 'C' | 'E' | 'A' | 'N';

const DOMAIN_INSIGHTS: Record<DomainKey, {
  hi: string; lo: string; hiTip?: string; loTip?: string;
}> = {
  O: {
    hi: 'Вы из тех, кого тянет к новому: идеи, инструменты, точки зрения. Рутина и однообразие вас утомляют. Вы видите возможности там, где другие видят препятствия.',
    lo: 'Вы практичны и脚踏实地. Предпочитаете проверенное — новому, конкретное — абстрактному. Не гонитесь за трендами, цените надёжность.',
    hiTip: 'Ваша суперсила — видеть нестандартные решения. Используйте это, чтобы помогать команде выходить за рамки.',
    loTip: 'Ваша сила — фокус. Когда все отвлекаются на «новомодное», вы доводите дело до конца.',
  },
  C: {
    hi: 'Вы организованны и доводите начатое до конца. На вас можно положиться: дедлайн — это дедлайн, а не «примерно».',
    lo: 'Вы гибки и спонтанны. Жёсткие рамки и списки задач — не ваша история. Лучше работаете в потоке, чем по графику.',
    hiTip: 'Ваша надёжность — ваш главный актив. Но не перегружайте себя: учитесь говорить «нет».',
    loTip: 'Ваша гибкость — преимущество в хаосе. Но для важных дел ставьте внешние дедлайны.',
  },
  E: {
    hi: 'Вы заряжаетесь от людей. Общение, команды, события — ваша стихия. В одиночестве быстро скучаете.',
    lo: 'Вы интроверт. Энергерия приходит изнутри, в тишине и фокусе. Шумные толпы истощают, глубокий разговор с одним человеком — наоборот.',
    hiTip: 'Используйте свой магнетизм: вы отлично мотивируете команды и ведёте за собой.',
    loTip: 'Вы работаете глубже, чем экстраверты. Защищайте своё время для фокуса.',
  },
  A: {
    hi: 'Вы дружелюбны и готовы помочь. Конфликты неприятны, сотрудничество — естественно. Люди считают вас тёплым и отзывчивым.',
    lo: 'Вы прямолинейны и конкурентны. Не боитесь сказать как есть, даже если это ранит. Цените честность выше такта.',
    hiTip: 'Ваша эмпатия склеивает команды. Но не в ущерб себе — учитесь отстаивать границы.',
    loTip: 'Ваша прямота экономит время. Но помните: не все воспринимают её одинаково.',
  },
  N: {
    hi: 'Вы чувствительны к стрессу. Эмоции глубокие, переживания сильные. Это даёт вам эмпатию, но может выматывать.',
    lo: 'Вы эмоционально устойчивы. Давление, неопределённость, кризисы — вы держитесь ровно. Редко теряете голову.',
    hiTip: 'Ваша чувствительность — источник эмоционального интеллекта. Но найдите практики сброса: спорт, медитация, прогулки.',
    loTip: 'В кризисе вы — якорь команды. Но не игнорируйте сигналы о выгорании.',
  },
};

export function domainInsight(key: DomainKey, value: number, pct: number) {
  const meta = DOMAIN_INSIGHTS[key];
  const isHigh = value >= 50;
  const lv = level(pct || value);
  return {
    text: isHigh ? meta.hi : meta.lo,
    tip: isHigh ? meta.hiTip : meta.loTip,
    ofTen: lv.ofTen,
    word: lv.word,
  };
}

// ── Derived метрики (лидерство, стрессоустойчивость и т.д.) ──
type DerivedKey =
  | 'leadership' | 'stressResistance' | 'creativity'
  | 'discipline' | 'socialSkills' | 'empathy' | 'businessReliability';

const DERIVED_INSIGHTS: Record<DerivedKey, {
  label: string;
  hi: string; lo: string; mid: string;
}> = {
  leadership: {
    label: 'Лидерство',
    hi: 'Вы естественно берёте ответственность. Люди склонны следовать за вами, даже без формальных полномочий.',
    lo: 'Вы не стремитесь вести за собой — предпочитаете быть специалистом, а не руководителем. И это нормально.',
    mid: 'Вы можете вести за собой, когда ситуация требует. Лидерство для вас не потребность, а инструмент.',
  },
  stressResistance: {
    label: 'Стрессоустойчивость',
    hi: 'В кризисе вы не теряете голову. Давление мобилизует вас, а не ломает.',
    lo: 'Стресс hit вас ощутимо. Вы не «железный» — и это нормально. Важно знать свои пределы и вовремя отдыхать.',
    mid: 'Вы выдерживаете умеренное давление, но в затяжных кризисах начинает копиться усталость.',
  },
  creativity: {
    label: 'Креативность',
    hi: 'Вы генерируете идеи легко. Ваша сильная сторона — находить нестандартные ходы.',
    lo: 'Вы не «идеейный генератор». Ваша сила — в реализации и доведении чужих идей до ума.',
    mid: 'Вы креативны в своей области — особенно когда хорошо знаете тему.',
  },
  discipline: {
    label: 'Дисциплина',
    hi: 'Вы доводите начатое до конца. Если взялись — сделаете. Список задач — ваш друг.',
    lo: 'Вы спонтанны. Жёсткие планы и графики работают плохо — вам нужен интерес, а не распорядок.',
    mid: 'В важных делах вы дисциплинированны, в мелочах — расслаблены. Баланс работает на вас.',
  },
  socialSkills: {
    label: 'Социальные навыки',
    hi: 'Вам легко с людьми. Нетворкинг, переговоры, презентации — ваша зона комфорта.',
    lo: 'Вы предпочитаете работу solo или с близкими людьми. Большие группы — не ваша стихия.',
    mid: 'У вас есть базовые социальные навыки, но масштабированное общение требует энергии.',
  },
  empathy: {
    label: 'Эмпатия',
    hi: 'Вы тонко чувствуете других. Часто понимаете, что человек хочет сказать, раньше, чем он сам.',
    lo: 'Вы ориентированы на задачу, а не на чувства. Это делает вас эффективным, но иногда резким.',
    mid: 'Вы понимаете чужие эмоции, если на них указать. Сами — не всегда их «считываете».',
  },
  businessReliability: {
    label: 'Деловая надёжность',
    hi: 'На вас можно положиться. Если пообещали — сделаете, даже если inconvenient.',
    lo: 'Вы гибки с обязательствами. Может перестроиться, если что-то важнее. Иногда это раздражает партнёров.',
    mid: 'В ключевых обязательствах вы надёжны, в мелочах можете забыть.',
  },
};

export function derivedInsight(key: string, value: number) {
  const meta = DERIVED_INSIGHTS[key as DerivedKey];
  if (!meta) return null;
  let bucket: 'hi' | 'lo' | 'mid';
  if (value >= 65) bucket = 'hi';
  else if (value < 40) bucket = 'lo';
  else bucket = 'mid';
  return { label: meta.label, text: meta[bucket] };
}

// ── DISC ─────────────────────────────────────────────────────
export const DISC_TYPES: Record<string, {
  title: string;
  subtitle: string;
  color: string;
  description: string;
  strengths: string[];
  growthAreas: string[];
  workStyle: string;
}> = {
  D: {
    title: 'Достигатор',
    subtitle: 'Результат — превыше всего',
    color: '#dc2626',
    description: 'Вы — человек действия. Решения принимаете быстро, конфликтов не избегаете, потому что для вас главное — дойти до цели. Рутина и долгие обсуждения вас утомляют: вы предпочитаете делать, а не говорить. Окружающие видят в вас того, кто берёт ответственность и тащит проект за собой. Ваша энергия — в вызовах: чем сложнее задача, тем интереснее.',
    strengths: ['Прямолинейность и честность', 'Скорость решений', 'Умение брать ответственность', 'Воля и настойчивость', 'Фокус на результате'],
    growthAreas: ['Может давить на людей, не замечая', 'Терпение — ваша слабая сторона', 'Иногда пропускаете детали ради скорости', 'Сложно делегировать — проще сделать самому'],
    workStyle: 'Вам нужен простор и автономия. Микроменеджмент убивает вашу мотивацию. Лучше всего работаете, когда видите чёткую цель и имеете свободу в выборе пути.',
  },
  I: {
    title: 'Вдохновитель',
    subtitle: 'Люди и эмоции — ваша стихия',
    color: '#f59e0b',
    description: 'Вы заряжаете атмосферу энергией. Там, где вы — шум, смех, идеи, движение. Вы легко находите общий язык с кем угодно, от CEO до курьера, и умеете увлечь других своей идеей. Вам важна не просто работа, а работа, которая зажигает. Монотонность и изоляция для вас — пытка. Окружающие тянутся к вам за позитивом и ощущением, что «всё получится».',
    strengths: ['Харизма и обаяние', 'Умение вдохновлять и мотивировать', 'Нетворкинг — легко заводите связи', 'Оптимизм и энергия', 'Спонтанность и креативность'],
    growthAreas: ['Может не доводить начатое до конца', 'Трудно с деталями и фактами', 'Иногда больше говорите, чем делаете', 'Может не замечать, что собеседник устал'],
    workStyle: 'Вам нужна команда и динамика. Одиночная работа в тишине — не ваша среда. Расцветаете в проектах, где есть люди, общение, презентации.',
  },
  S: {
    title: 'Стабилизатор',
    subtitle: 'Тихая опора команды',
    color: '#059669',
    description: 'Вы — тот человек, без которого команда разваливается. Спокойные, надёжные, предсказуемые — вы создаете фон стабильности, на котором другие могут работать. Вы не ищете славы и не рвётесь в лидеры, но когда всё вокруг горит, именно вы спокойно тушите пожар. Не любите перемен ради перемен: для вас «работающее» ценнее «инновационного». Окружающие ценят вас за верность и ощущение безопасности рядом.',
    strengths: ['Надёжность и постоянство', 'Терпение и выдержка', 'Умение слушать', 'Командная лояльность', 'Спокойствие в кризисе'],
    growthAreas: ['Может избегать конфликтов, когда они нужны', 'Сложно адаптироваться к резким переменам', 'Трудно сказать «нет»', 'Может накапливать обиды, не высказывая их'],
    workStyle: 'Вам нужна предсказуемость и стабильная команда. Частые смены приоритетов и хаос — ваш стресс. Лучше всего работаете в чётких, устоявшихся процессах.',
  },
  C: {
    title: 'Аналитик',
    subtitle: 'Точность и качество — ваш закон',
    color: '#2563eb',
    description: 'Вы — тот, кто проверяет дважды. Качество для вас важнее скорости, а правильность — важнее скорости. Вы мыслите системно: видите процессы, находите ошибки, выстраиваете порядок из хаоса. Не любите работать на бегу — вам нужно время подумать, проверить, убедиться. Окружающие могут считать вас перфекционистом, но именно благодаря вам проекты не разваливаются от небрежности. Рутина вас не пугает — пугает небрежность.',
    strengths: ['Точность и внимательность к деталям', 'Системное мышление', 'Аналитический подход', 'Высокие стандарты качества', 'Объективность — решение на фактах, не эмоциях'],
    growthAreas: ['Перфекционизм может тормозить', 'Может застрять в «анализе паралича»', 'Сложно принимать решения без полных данных', 'Избегание риска может стоить возможностей'],
    workStyle: 'Вам нужны чёткие правила, доступ к данным и время на анализ. Хаос и «решения на коленке» — ваш кошмар. Расцветаете там, где требуется точность и системность.',
  },
};

/** Комбинация primary + secondary типа */
export function discCombination(primary: string, secondary: string): string {
  const combos: Record<string, string> = {
    'DC': 'Вы нацелены на результат, но не забываете о качестве. Редкое сочетание скорости и точности.',
    'CD': 'Качество и результат для вас одинаково важны. Вы добиваетесь целей, не жертвуя стандартами.',
    'DI': 'Вы не только достигаете целей, но и умеете увлечь за собой других. Прирождённый лидер.',
    'ID': 'Вы заряжаете людей энергией и при этом двигаетесь к цели. Харизматичный достигатор.',
    'DS': 'Вы целеустремлённы, но при этом надёжны и стабильны. Команда знает: если вы сказали — сделаете.',
    'SD': 'Вы стабильны и при этом умеете брать инициативу. Тихий, но надёжный лидер.',
    'IC': 'Вы общительны, но при этом точны и системны. Умеете презентовать сложные вещи просто.',
    'CI': 'Вы системны и точны, но при этом умеете донести идею до людей. Хороший аналитик-коммуникатор.',
    'IS': 'Вы тёплый, чуткий командный игрок. Люди чувствуют себя с вами комфортно и безопасно.',
    'SI': 'Вы создаёте атмосферу уюта и позитива в команде. Душа коллектива.',
    'SC': 'Вы надёжны, точны и спокойны. Идеальный исполнитель сложных задач.',
    'CS': 'Вы системны и стабильны. Тот, на ком держатся процессы.',
  };
  return combos[primary + secondary] || '';
}

const DISC_INSIGHTS: Record<string, string> = Object.fromEntries(
  Object.entries(DISC_TYPES).map(([k, v]) => [k, v.description])
);

export function discInsight(primary: string): string {
  return DISC_INSIGHTS[primary] || '';
}

// ── EQ ───────────────────────────────────────────────────────
export function eqInsight(totalPct: number): string {
  if (totalPct >= 75) return 'У вас высокий эмоциональный интеллект. Вы понимаете свои и чужие эмоции и умеете их регулировать.';
  if (totalPct >= 50) return 'У вас средний эмоциональный интеллект. Базовые навыки есть, но есть куда расти — особенно в понимании эмоций других.';
  return 'Эмоциональный интеллект — зона для развития. Это не приговор: EQ можно прокачать практиками осознанности и рефлексии.';
}

// ── Schwartz ─────────────────────────────────────────────────
const SCHWARTZ_INSIGHTS: Record<string, { label: string; text: string }> = {
  self_direction: { label: 'Самостоятельность', text: 'Свобода мыслить и действовать независимо — ваша ключевая ценность. Вы не любите, когда вам указывают.' },
  stimulation: { label: 'Стимуляция', text: 'Вам нужен драйв и новизна. Рутина вас убивает — вы ищете острых ощущений и нового опыта.' },
  hedonism: { label: 'Гедонизм', text: 'Удовольствие и наслаждение жизнью — важно для вас. Вы не откладываете жизнь на потом.' },
  achievement: { label: 'Достижение', text: 'Успех и компетентность — ваши драйверы. Вам важно быть лучшим в том, что вы делаете.' },
  power: { label: 'Власть', text: 'Статус и контроль над ресурсами имеют для вас значение. Вы стремитесь к влиянию.' },
  security: { label: 'Безопасность', text: 'Стабильность и порядок — фундамент для вас. Вы цените предсказуемость и гармонию в обществе.' },
  conformity: { label: 'Конформизм', text: 'Вы склонны следовать правилам и нормам. Не любите нарушать ожидания других.' },
  tradition: { label: 'Традиции', text: 'Уважение к традициям и устоям важно для вас. Вы цените преемственность поколений.' },
  benevolence: { label: 'Благожелательность', text: 'Забота о близких — ваш приоритет. Вы вкладываетесь в отношения и готовы помогать.' },
  universalism: { label: 'Универсализм', text: 'Справедливость и равенство — ваши ценности. Вам небезразлична судьба людей и планеты.' },
};

export function schwartzInsight(primaryValue: string): { label: string; text: string } | null {
  return SCHWARTZ_INSIGHTS[primaryValue] || null;
}

// ── Сводка для главной ───────────────────────────────────────
/** Возвращает топ-3 сильные стороны и топ-2 зоны роста из derived метрик */
export function topStrengthsAndGrowth(derived: Record<string, number>): {
  strengths: { label: string; text: string }[];
  growth: { label: string; text: string }[];
} {
  const entries = Object.entries(derived)
    .filter(([, v]) => v != null && v !== undefined)
    .sort(([, a], [, b]) => (b as number) - (a as number));

  const strengths = entries
    .filter(([, v]) => (v as number) >= 60)
    .slice(0, 3)
    .map(([k, v]) => derivedInsight(k, v as number))
    .filter(Boolean) as { label: string; text: string }[];

  const growth = entries
    .filter(([, v]) => (v as number) < 50)
    .slice(-2)
    .map(([k, v]) => derivedInsight(k, v as number))
    .filter(Boolean) as { label: string; text: string }[];

  return { strengths, growth };
}
